import React, { useState, useEffect, useCallback } from 'react';
import { useCart } from '../context/CartContext.tsx';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import {
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Box,
  Select,
  TextField,
  Container,
  MenuItem,
} from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';

// Importera eller definiera dessa beroenden
const printfulTeeImage = "https://files.cdn.printful.com/files/b89/b899c3824e2130793dd74c0d3dbe918d_preview.png";
  const getHighResImage = (file) => {
    if (!file?.thumbnail_url) return "/placeholder.jpg";
    return file.thumbnail_url.replace("_thumb.png", "_preview.png");
  };

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
const apiBaseUrl = process.env.REACT_APP_BACKEND_URL || 'https://own-project.onrender.com';

const Cart = () => {
  const { cartItems, removeFromCart, updateQuantity, updateCartItem } = useCart();

  const [customerData, setCustomerData] = useState({
    name: '',
    email: '',
    address: '',
    phone: '',
    city: '',
    country: '',
    zip: '',
    countryCode: '',
  });

  const [shippingCost, setShippingCost] = useState([]);
  const [selectedShippingOption, setSelectedShippingOption] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculatePriceWithVAT = (price) => Math.round(parseFloat(price) * 1.25);

  const calculateTotal = () =>
    cartItems.reduce((total, item) => total + calculatePriceWithVAT(item.selectedPrice) * item.quantity, 0);

  const calculateTotalWithShipping = () => {
    const shipping = selectedShippingOption ? parseFloat(selectedShippingOption.rate) : 0;
    return calculateTotal() + shipping;
  };

  const getShippingCost = useCallback(async () => {
    if (!customerData.countryCode || cartItems.length === 0) return;
    setLoading(true);
    try {
      const response = await axios.post(`${apiBaseUrl}/api/shipping`, {
        customerData,
        cartItems,
      });

      if (response.status === 200 && response.data?.shippingCost?.length > 0) {
        setShippingCost(response.data.shippingCost);
        setSelectedShippingOption(response.data.shippingCost[0]);
      } else {
        throw new Error('Fraktalternativ kunde inte hämtas.');
      }
    } catch (error) {
      console.error('Fel vid hämtning av fraktkostnad:', error);
      alert(`Ett fel uppstod vid hämtning av fraktkostnad: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  }, [customerData, cartItems]);

  useEffect(() => {
    const timeoutId = setTimeout(getShippingCost, 500);
    return () => clearTimeout(timeoutId);
  }, [cartItems, customerData.countryCode, getShippingCost]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'country') {
      const countryCodes = { Sverige: 'SE', 'United States': 'US', Germany: 'DE' };
      setCustomerData((prev) => ({ ...prev, country: value, countryCode: countryCodes[value] || '' }));
    } else {
      setCustomerData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDecreaseQuantity = (uniqueId, currentQuantity) => {
    currentQuantity <= 1 ? removeFromCart(uniqueId) : updateQuantity(uniqueId, -1);
  };

  const handleSizeChange = (uniqueId, newSize) => {
    const item = cartItems.find((i) => i.uniqueId === uniqueId);
    if (!item) return;

    const variant = item.variants?.find(
      (v) => v.size?.toLowerCase().trim() === newSize.toLowerCase().trim()
    );

    if (!variant) return alert(`Ingen variant hittades för storleken ${newSize}`);

    const newVariantId = variant.variant_id || variant.variantId;

    const newPrice = variant.price || variant.selectedPrice || item.selectedPrice;

    updateCartItem(uniqueId, {
      selectedSize: newSize,
      size: newSize,
      variant_id: newVariantId,
      selectedPrice: newPrice,
    });
  };

  const handleCheckout = async () => {
    if (cartItems.length === 0) return alert('Din varukorg är tom.');
    if (Object.values(customerData).some((f) => f.trim() === '')) return alert('Vänligen fyll i alla kontaktuppgifter.');

    const userData = JSON.parse(localStorage.getItem('user'));
    const userId = userData ? userData.userId : null;

    try {
      const stripe = await stripePromise;
      const response = await axios.post(`${apiBaseUrl}/api/checkout/create-checkout-session`, {
        cartItems: cartItems.map((item) => ({
          ...item,
          price_with_vat: calculatePriceWithVAT(item.selectedPrice),
          design_url: item.designFileUrl || item.thumbnail_url || null,
        })),
        customerInfo: customerData,
        shippingCost: selectedShippingOption ? parseFloat(selectedShippingOption.rate) : 0,
        userId,
      });
      if (response.data?.id) await stripe.redirectToCheckout({ sessionId: response.data.id });
      else alert('Ett fel uppstod vid skapandet av checkout-session.');
    } catch (error) {
      console.error('Fel vid checkout:', error.response?.data || error.message);
      alert('Ett fel uppstod vid betalning: ' + (error.response?.data?.error || error.message));
    }
  };

  return (
    <Container maxWidth="md" sx={{ minHeight: 'calc(100vh - 80px)', paddingBottom: '80px' }}>
      <Box sx={{ padding: 2, margin: 'auto', overflow: 'auto' }}>
        <Typography variant="h4" gutterBottom align="center">Din Varukorg</Typography>

        {cartItems.length === 0 ? (
          <Typography align="center">Din varukorg är tom.</Typography>
        ) : (
          <Grid container spacing={2} justifyContent="center">
            {cartItems.map((item) => {
              // Bestäm vilken bild som ska visas
              const displayedImage =
                item.name === "Unisex palestine t-shirt"
                  ? printfulTeeImage
                  : getHighResImage(item.previewFile) || item.selectedVariantImage || "/placeholder.jpg";

              return (
                <Grid item xs={12} sm={6} key={item.uniqueId}>
                  <Card sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 2 }}>
                    <img
                      src={displayedImage}
                      alt={item.name}
                      style={{ width: '100%', maxWidth: '200px', objectFit: 'cover', marginBottom: '1rem' }}
                    />
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{item.name}</Typography>
                      <Box sx={{ marginBottom: 1 }}>
                        <Typography variant="body2" color="textSecondary">Storlek:</Typography>
                        <Select
                          value={item.selectedSize || ''}
                          onChange={(e) => handleSizeChange(item.uniqueId, e.target.value)}
                          fullWidth size="small" displayEmpty sx={{ marginTop: 1 }}
                        >
                          <MenuItem value="" disabled>Välj storlek</MenuItem>
                          {item.availableSizes?.map((size) => (
                            <MenuItem key={size} value={size}>{size}</MenuItem>
                          ))}
                        </Select>
                      </Box>
                      <Typography variant="body2" color="textSecondary">Färg: {item.selectedColor}</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 1 }}>
                        <IconButton onClick={() => handleDecreaseQuantity(item.uniqueId, item.quantity)}><RemoveIcon /></IconButton>
                        <Typography>{item.quantity}</Typography>
                        <IconButton onClick={() => updateQuantity(item.uniqueId, 1)}><AddIcon /></IconButton>
                      </Box>
                      <Typography variant="body2" color="textSecondary">
                        Pris per produkt: {item.selectedPrice ? `${calculatePriceWithVAT(item.selectedPrice)} SEK (inkl. moms)` : 'Ej tillgängligt'}
                      </Typography>
                      <Typography sx={{ fontWeight: 'bold', marginTop: 1 }}>
                        Totalt pris: {calculatePriceWithVAT(item.selectedPrice) * item.quantity} SEK
                      </Typography>
                      <Button variant="outlined" color="error" fullWidth sx={{ mt: 2 }} onClick={() => removeFromCart(item.uniqueId)}>Ta bort</Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {cartItems.length > 0 && (
          <Box sx={{ mt: 3, width: '100%', textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>Totalbelopp: {calculateTotal()} SEK</Typography>
            <Typography variant="h6" sx={{ mb: 2 }}>Dina uppgifter</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
              {['name', 'email', 'address', 'phone', 'city', 'country', 'zip'].map((field) => (
                <TextField
                  key={field}
                  name={field}
                  label={field.charAt(0).toUpperCase() + field.slice(1)}
                  fullWidth
                  margin="dense"
                  value={customerData[field]}
                  onChange={handleInputChange}
                  sx={{ maxWidth: { xs: '100%', sm: 400 } }}
                  required
                />
              ))}
            </Box>
            {loading && <Typography>Hämtar fraktkostnad...</Typography>}
            {shippingCost.length > 0 && selectedShippingOption && (
              <>
                <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Fraktkostnad: {selectedShippingOption.rate} SEK</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                  Total summa (inkl. frakt): {calculateTotalWithShipping().toFixed(2)} SEK
                </Typography>
              </>
            )}
     <Button
  variant="contained"
  onClick={handleCheckout}
  disabled={loading}
  sx={{
    fontWeight: "bold",
    fontSize: "1rem",
    borderRadius: 2,
    paddingY: 1.2,
    paddingX: 3, // lite horisontellt padding för att inte vara för smal
    background: "linear-gradient(90deg, #1e3a8a, #3b82f6)", // djupblå → klarblå
    color: "#fff",
    boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
    textTransform: "none",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "linear-gradient(90deg, #3b82f6, #1e3a8a)", // inverterad på hover
      boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
      transform: "translateY(-2px)",
    },
    "&:active": {
      transform: "translateY(0px)",
      boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
    },
    "&.Mui-disabled": {
      background: "rgba(100, 100, 100, 0.4)",
      color: "#ccc",
      boxShadow: "none",
    },
  }}
>
  Betala nu
</Button>

          </Box>
        )}
      </Box>
    </Container>
  );
};

export default Cart;
